import { MouseEvent, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  webcamsFeed,
  webcamsInformations,
  webcamsInformations_webcams,
} from "../../Api/types";
import { api_postValueOrErrorMessage } from "../../Api/util";
import usePlatform from "../../Hook/platform";
import { NAVIGATE } from "../../Router";
import Loader from "../Loader";
import "./adsbanners.css";

type WebcamPreview = {
  name?: string;
  thumbnail: string;
  viewers: number;
};

const FALLBACK_PREVIEW: WebcamPreview[] = [
  { thumbnail: "/logo192.png", viewers: 812 },
  { thumbnail: "/logo192.png", viewers: 654 },
  { thumbnail: "/logo192.png", viewers: 529 },
];

const asPreview = (cam: webcamsInformations_webcams): WebcamPreview | null => {
  if (!cam.thumbnail) return null;
  return {
    name: cam.name,
    thumbnail: cam.thumbnail,
    viewers: cam.nb_connected || 0,
  };
};

function WebcamBannerNative() {
  const [preview, setPreview] = useState<WebcamPreview[]>([]);
  const platform = usePlatform();
  const providerCodes = (
    platform?.platform?.highlighted_webcams_providers || []
  ).map((provider) => provider.provider);
  const providerSignature = providerCodes.join("|");

  const openWebcamsPage = () => {
    if (NAVIGATE.navigate) NAVIGATE.navigate("/webcams");
  };

  const openCam = (event: MouseEvent) => {
    event.stopPropagation();
    openWebcamsPage();
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const defaultSearch = api_postValueOrErrorMessage<webcamsFeed>(
          "webcams/search",
          {
            limit: 1,
            offset: 0,
            genders: ["woman"],
            statutes: ["public"],
            sort: "nb_connected",
            qualities: ["sd", "hd"],
          },
        );

        const idsByProvider = await Promise.all(
          providerCodes.map((provider) =>
            api_postValueOrErrorMessage<webcamsFeed>("webcams/highlighted", {
              limit: 1,
              offset: 0,
              provider,
              genders: ["woman", "couple", "trans"],
              statutes: ["public"],
            }),
          ),
        );

        const defaultIds = await defaultSearch;

        const webcamIds = [idsByProvider[0], defaultIds, idsByProvider[1]]
          .map((feed) => feed.webcams?.[0])
          .filter((id): id is string => Boolean(id));

        const uniqueWebcamIds = webcamIds.filter(
          (id, index, ids) => ids.indexOf(id) === index,
        );

        if (!uniqueWebcamIds.length) {
          throw new Error("No webcams found");
        }

        const info = await api_postValueOrErrorMessage<webcamsInformations>(
          "webcams/informations",
          { webcams: uniqueWebcamIds },
        );

        const infosById = new Map(
          (info.webcams || []).map((cam) => [cam.id, cam]),
        );

        const withThumb = uniqueWebcamIds
          .map((id) => infosById.get(id))
          .filter((cam): cam is webcamsInformations_webcams => Boolean(cam))
          .map(asPreview)
          .filter((cam): cam is WebcamPreview => Boolean(cam));

        const selected = withThumb.slice(0, 3);

        if (!cancelled) {
          setPreview(selected.length ? selected : FALLBACK_PREVIEW);
        }
      } catch {
        if (!cancelled) {
          setPreview(FALLBACK_PREVIEW);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [providerSignature]);

  const previewItems = preview.length ? preview : FALLBACK_PREVIEW;
  const showFallbackPreview = previewItems === FALLBACK_PREVIEW;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openWebcamsPage}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openWebcamsPage();
        }
      }}
      className="insite-ads-banner-card insite-ads-banner-card-native w-full max-w-[320px] rounded-2xl border border-[#f4c0ac] bg-gradient-to-br from-[#fff4ed] via-[#ffe7da] to-[#ffd9c7] px-4 py-3 text-left shadow-[0_16px_36px_rgba(164,84,52,0.22)] transition-transform duration-300 hover:-translate-y-1 sm:max-w-[728px] sm:px-6 sm:py-4"
    >
      <div className="insite-ads-banner-shine" />
      <div className="relative z-[1] flex flex-col gap-3 sm:gap-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-end justify-between gap-3">
            <p className="text-sm md:text-xl font-semibold uppercase tracking-[0.28em] text-[#934b2f]">
              <FormattedMessage
                id="ads_webcam_native_kicker"
                defaultMessage="Cam shows en direct"
              />
            </p>
          </div>
          <div className="hidden md:flex items-center justify-end gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#5f2b19] px-4 py-2 text-[11px] font-bold text-white sm:px-5 sm:text-xs">
              <FormattedMessage
                id="ads_webcam_native_cta"
                defaultMessage="Voir nos cams en ligne"
              />
              <span aria-hidden>→</span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2">
          {previewItems.slice(0, 3).map((cam, index) => (
              <button
                type="button"
                key={`${cam.name}-${index}`}
                onClick={openCam}
                className="overflow-hidden rounded-lg border border-black/10 bg-black/5 text-left"
              >
                {showFallbackPreview ? (
                  <div className="flex h-20 w-full items-center justify-center bg-[#fff6f1] sm:h-40">
                    <Loader black />
                  </div>
                ) : (
                  <img
                    className="h-20 w-full object-cover sm:h-40"
                    src={cam.thumbnail}
                    alt={cam.name || "webcam"}
                    loading="lazy"
                  />
                )}
                <div className="flex items-center justify-between gap-2 bg-[#fff6f1] px-2 py-1 text-[10px] text-[#6e3320] sm:text-xs">
                  <span className="truncate font-semibold">
                    {cam.name || (
                      <FormattedMessage
                        id="ads_webcam_native_model_fallback"
                        defaultMessage="Live model"
                      />
                    )}
                  </span>
                  {Boolean(cam.viewers) && (
                    <span className="whitespace-nowrap">{cam.viewers}+</span>
                  )}
                </div>
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}

export default WebcamBannerNative;
